import { css, useTheme } from '@emotion/react';
import { Pause, Play } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { fontSizes, spacing } from '@/theme/tokens';

const PACE_OPTIONS = [
  { wpm: 120, label: '120 WPM' },
  { wpm: 150, label: '150 WPM' },
  { wpm: 180, label: '180 WPM' },
] as const;

interface SkipRehearsalCardProps {
  scriptContent: string;
  currentWpm: number;
  onSkip: (wpm: number) => void;
  onCancel: () => void;
}

function getFirstTwoSentences(text: string): string {
  const cleaned = text.replace(/---/g, '').trim();
  const sentences = cleaned.match(/[^.!?]+[.!?]+/g);
  if (!sentences) return cleaned.slice(0, 200);
  return sentences.slice(0, 2).join('').trim();
}

function wpmToSpeechRate(wpm: number): number {
  // Web Speech API rate: 1.0 is normal (~150 WPM)
  return wpm / 150;
}

export function SkipRehearsalCard({
  scriptContent,
  currentWpm,
  onSkip,
  onCancel,
}: SkipRehearsalCardProps) {
  const theme = useTheme();
  const [selectedWpm, setSelectedWpm] = useState(currentWpm || 150);
  const [customWpm, setCustomWpm] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [playingWpm, setPlayingWpm] = useState<number | null>(null);
  const synthRef = useRef<SpeechSynthesisUtterance | null>(null);

  const previewText = getFirstTwoSentences(scriptContent);

  const stopPreview = useCallback(() => {
    window.speechSynthesis.cancel();
    setPlayingWpm(null);
    synthRef.current = null;
  }, []);

  const playPreview = useCallback(
    (wpm: number) => {
      stopPreview();
      if (!window.speechSynthesis || !previewText) return;

      const utterance = new SpeechSynthesisUtterance(previewText);
      utterance.rate = wpmToSpeechRate(wpm);
      utterance.onend = () => setPlayingWpm(null);
      utterance.onerror = () => setPlayingWpm(null);
      synthRef.current = utterance;
      setPlayingWpm(wpm);
      window.speechSynthesis.speak(utterance);
    },
    [previewText, stopPreview],
  );

  const handleContinue = () => {
    stopPreview();
    const wpm = isCustom ? parseInt(customWpm, 10) || 150 : selectedWpm;
    onSkip(Math.max(80, Math.min(250, wpm)));
  };

  const effectiveWpm = isCustom ? parseInt(customWpm, 10) || 0 : selectedWpm;
  const isValid = effectiveWpm >= 80 && effectiveWpm <= 250;

  return (
    <div
      css={css`
        border: 1px solid ${theme.colors.border};
        border-radius: 12px;
        background: ${theme.colors.surface};
        padding: ${spacing.lg};
        margin-bottom: ${spacing.md};
      `}
      data-testid="skip-rehearsal-card"
    >
      <h3
        css={css`
          margin: 0 0 ${spacing.xs};
          font-size: ${fontSizes.md};
          color: ${theme.colors.text};
          font-weight: 600;
        `}
      >
        Choose Speaking Pace
      </h3>
      <p
        css={css`
          margin: 0 0 ${spacing.md};
          font-size: ${fontSizes.sm};
          color: ${theme.colors.textSecondary};
          line-height: 1.5;
        `}
      >
        How fast will you deliver this speech?
      </p>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.sm};
          margin-bottom: ${spacing.md};
        `}
        role="radiogroup"
        aria-label="Speaking pace"
      >
        {PACE_OPTIONS.map(({ wpm, label }) => (
          <label
            key={wpm}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.sm};
              padding: ${spacing.sm} ${spacing.md};
              border-radius: 8px;
              border: 1px solid
                ${!isCustom && selectedWpm === wpm ? theme.colors.primary : theme.colors.border};
              background: ${!isCustom && selectedWpm === wpm
                ? `${theme.colors.primary}15`
                : 'transparent'};
              cursor: pointer;
              transition: all 0.15s ease;

              &:hover {
                border-color: ${theme.colors.primary};
              }
            `}
          >
            <input
              type="radio"
              name="pace"
              value={wpm}
              checked={!isCustom && selectedWpm === wpm}
              onChange={() => {
                setIsCustom(false);
                setSelectedWpm(wpm);
              }}
              css={css`
                accent-color: ${theme.colors.primary};
              `}
            />
            <span
              css={css`
                flex: 1;
                font-size: ${fontSizes.sm};
                font-weight: 500;
                color: ${theme.colors.text};
              `}
            >
              {label}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (playingWpm === wpm) {
                  stopPreview();
                } else {
                  playPreview(wpm);
                }
              }}
              aria-label={
                playingWpm === wpm ? `Stop preview at ${wpm} WPM` : `Preview at ${wpm} WPM`
              }
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                border-radius: 50%;
                border: none;
                background: transparent;
                color: ${playingWpm === wpm ? theme.colors.primary : theme.colors.textSecondary};
                cursor: pointer;

                &:hover {
                  color: ${theme.colors.text};
                }
              `}
            >
              {playingWpm === wpm ? (
                <Pause size={14} aria-hidden="true" />
              ) : (
                <Play size={14} aria-hidden="true" />
              )}
            </button>
          </label>
        ))}

        <label
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.sm};
            padding: ${spacing.sm} ${spacing.md};
            border-radius: 8px;
            border: 1px solid ${isCustom ? theme.colors.primary : theme.colors.border};
            background: ${isCustom ? `${theme.colors.primary}15` : 'transparent'};
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
              border-color: ${theme.colors.primary};
            }
          `}
        >
          <input
            type="radio"
            name="pace"
            value="custom"
            checked={isCustom}
            onChange={() => setIsCustom(true)}
            css={css`
              accent-color: ${theme.colors.primary};
            `}
          />
          <span
            css={css`
              font-size: ${fontSizes.sm};
              font-weight: 500;
              color: ${theme.colors.text};
            `}
          >
            Custom:
          </span>
          <input
            type="number"
            min={80}
            max={250}
            value={customWpm}
            onChange={(e) => {
              setIsCustom(true);
              setCustomWpm(e.target.value);
            }}
            placeholder="WPM"
            aria-label="Custom WPM"
            css={css`
              width: 70px;
              padding: 4px 8px;
              border: 1px solid ${theme.colors.border};
              border-radius: 4px;
              background: ${theme.colors.background};
              color: ${theme.colors.text};
              font-size: ${fontSizes.sm};

              &:focus {
                outline: none;
                border-color: ${theme.colors.primary};
              }
            `}
          />
        </label>
      </div>

      <div
        css={css`
          display: flex;
          gap: ${spacing.sm};
        `}
      >
        <Button
          onClick={handleContinue}
          disabled={!isValid}
          size="sm"
          css={css`
            flex: 1;
          `}
          data-testid="continue-button"
        >
          Continue to Music
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            stopPreview();
            onCancel();
          }}
          data-testid="cancel-skip-button"
        >
          Back
        </Button>
      </div>
    </div>
  );
}
